<template>
  <div class="page-invitation">

    <!-- Background -->
    <ui-wad class="overflow-hidden">
      <div class="page-invitation__background" />
    </ui-wad>

    <!-- Header -->
    <header class="page-invitation__header">

      <a
        class="page-invitation__logo"
        href="https://www.ghs.fr/"
      >
        <img :src="'/images/brand/logo_hamac_complete_by_ghs_wine-berry.svg'">
      </a>


    </header>

    <!-- Content -->
    <ui-cloak
      v-if="!fetchError"
      :is-visible="areDataFetched"
    >
      <template #default>

        <account-invitation-organism
          :is-super-admin="isSuperAdmin"
          :account-exists="accountExists"
          class="page-invitation__form"
        />

      </template>
    </ui-cloak>

    <!-- Fallback -->
    <account-invitation-fallback
      v-if="fetchError"
      class="page-invitation__fallback"
    />

  </div>
</template>

<style lang="sass" scoped>
@import '~/assets/sass/modules/layout/variables'

$logo-text-size: 180px
$form-width-md: map-get($breakpoints, 'tablet')
$form-width-sm: 632px

.page-invitation
  @extend %fit-waterline

  $_0: map-get($palette-layout, 'color-3')
  $_1: mix($background-primary, $_0, 97.5%)
  $_2: mix($background-primary, $_0, 92.5%)
  $_3: mix($background-primary, $_0, 80%)
  background: linear-gradient(to bottom, $_1, $_2 80%, $_3 160%)

  &__background
    @include no-interaction
    @include inset
    position: absolute
    background-size: cover
    opacity: .6
    background-image: image('/backgrounds/test.svg')
    background-position-x: -5vw

  &__header
    display: flex
    align-items: flex-start
    padding: space-x(2) space-x(4)

  &__logo
    @include clickable
    display: block
    margin-right: space-x(8)

    img
      width: $logo-text-size

  &__form
    width: $form-width-md
    margin: 0 auto

  &__fallback
    @include centered
    position: absolute

/// Animation
///
/// Fetching content period will not trigger any animation (YAGNI)
/// On fetching success, just pop an animation echoing something (like a mail)
/// popping onto a desk
.page-invitation__form
  animation: bounceInDown 1s ease-in-out
/// Fallback animation: simple, not wanting the user to remember this part
.page-invitation__fallback
  animation: fade-in 1s ease-in-out

/// Responsive
///
@include media('<laptop')

  .page-invitation__form
    width: $form-width-sm

@include media('<tablet')

  .page-invitation__header
    padding: space-x(2) space-x(2)
  .page-invitation__logo img
    width: calc($logo-text-size / 1.25)
  .page-invitation__form
    width: 100%
  .page-invitation__fallback
    flex-direction: column
  .page-invitation__fallback :deep(.fallback__left)
    margin-bottom: space-x(6)

@include media('<mobile')

  .page-invitation__logo
    margin-right: space-x(2)
    flex-shrink: 0

</style>

<script lang="ts">
import AccountInvitationFallback from '~/components/account/invitation/Fallback.vue'
import AccountInvitationOrganism from '~/components/account/invitation/Organism.vue'
import Console from '~/utils/lib/core/console'
import { equals, tapCatch } from '~/utils/lib/core/prelude'
import { useStore_account_invitation } from '~/stores/account/invitation'
import { useLegacyPageFetch } from '~/composables/core/hook'

const ERRORS = {
  MISC_REMOTE: 'misc remote error'
}

const console = new Console('component:invitation-page')

export default defineNuxtComponent({
  components: { AccountInvitationFallback, AccountInvitationOrganism },
  setup() {
    definePageMeta({
      layout: 'blank',
    })
    useHead({
      title: 'Invitation sur Hamac par GHS',
    })

    const {
      fetchState,
      fetchWrapper,
    } = useLegacyPageFetch()

    const store_invitation = useStore_account_invitation()

    return {
      fetchState,
      fetchWrapper,
      store_invitation,
    }
  },
  data() {
    return {
      ERRORS,
      areDataFetched: false,
      fetchError: '',
    }
  },
  computed: {
    isSuperAdmin() {
      return equals(this.$route.query.super_admin, 'true')
    },
    accountExists() {
      return equals(this.$route.query.login_exists, 'true')
    }
  },
  created() {
    this.fetchWrapper(
      () => {
        const invitationToken = this.$route.params.token as string

        this.store_invitation.$reset()

        return this.store_invitation.hydrate({ invitationToken })
          .then(() => this.areDataFetched = true)
          .catch(tapCatch(err => {
            console.warn('fetch', err)

            this.fetchError = ERRORS.MISC_REMOTE
          }))
      }
    )
  },
})
</script>
